import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import '@axtria/hqi-genai/lib/hqi-genai.css';
import { fetchFunctionsAndGoals, fetchRoles } from '../../actions/app.action';
import { SET_ACTIVE_MENU, SET_USER_INFO_KEY_VALUE } from '../../actions/app.type';
import { Home } from '@axtria/hqi-genai';
import Loader from '../../components/Loader';
import { TTYD_3 } from '../../constants/Constantsi18';
import { buildUserProfileDetails } from '../../utils/userProfileDetails';
import { toPortfolioRoles, resolveSelectedRole } from '../../utils/roleAdapter';
import './home.scss';


const baseURL = process.env.REACT_APP_HQI_BASE_URL;
const middlewareURL = process.env.REACT_APP_HQI_MIDDLEWARE_URL;
const UMUrl = process.env.REACT_API_URL;

const HomeComponent = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { activeKnowledgeId, activeApp } = useSelector((state: any) => ({
    activeKnowledgeId: state.loginResource.activeKnowledgeId,
    activeApp: state.loginResource.activeApp,
  }));
  const userInfo = useSelector((state: any) => state.loginResource);
  const functionsAndGoals = useSelector((state: any) => state.loginResource?.functionsAndGoals);
  const isUserGroupDataPending = useSelector((state: any) =>
    state.loginResource?.userGroupList === undefined ||
    state.loginResource?.userGroupMapping === undefined
  );
  const editProfileTriggered = useSelector((state: any) => !!state.loginResource?.editProfileTriggered);
  const [isFuncsLoading, setIsFuncsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentProjectGroupIds: any[] = useMemo(() =>
    (userInfo?.userGroupMapping || [])
      .find((pg: any) => pg.project_id == userInfo?.preferredProject)  ?.user_group_ids || [],
      //.find((pg: any) => pg.project_id == 331)?.user_group_ids || [],
    [userInfo?.userGroupMapping]
  );
  const userGroupNames = useMemo(() =>
    (userInfo?.userGroupList || [])
      .filter((g: any) => currentProjectGroupIds.includes(g.usergroup_id))
      .map((g: any) => g.user_group_name),
    [userInfo?.userGroupList, currentProjectGroupIds]
  );
  const currentProject = useMemo(() =>
    userInfo?.company_data
      ?.find((cd: any) => cd.companyId === userInfo?.preferredCompany)
      ?.projectList?.find((p: any) => p.project_id === userInfo?.preferredProject),
    [userInfo?.company_data, userInfo?.preferredCompany, userInfo?.preferredProject]
  );
  const isOnboardingEnabled = currentProject?.onboarding && currentProject?.onboarding !== false;

  // Options come from the role master (/umbackend/role); the profileData list is kept
  // only as a fallback for the window before that call resolves.
  const userPortfolioRoles = useMemo(() => {
    const roles = toPortfolioRoles(userInfo?.roleList);
    if (roles.length) return roles;

    return userInfo?.profileData ? userInfo.profileData.filter((role: any) => role.id !== 18) : [];
  }, [userInfo?.roleList, userInfo?.profileData]);

  const projectBrandList = useMemo(() =>
    userInfo?.company_data
      ?.find((cd: any) => cd.companyId === userInfo?.preferredCompany)
      ?.projectList?.find((p: any) => p.project_id === userInfo?.preferredProject)?.brands,
    [userInfo?.company_data, userInfo?.preferredCompany, userInfo?.preferredProject]
  );

  const isWorkspaceOwner = useMemo(() =>
    userInfo?.userProfile
      ?.filter((up: any) => up.companyId === userInfo?.preferredCompany)
      .some((up: any) => up.projectProfile.some((pp: any) => pp.project_id === userInfo?.preferredProject && pp.profile_id === 18)),
    [userInfo?.userProfile, userInfo?.preferredCompany, userInfo?.preferredProject]
  );

  const userPortfolio = useMemo(() => {
    const base = userInfo?.userPortfolios?.filter(
      (pf: any) => pf.project_id == userInfo?.preferredProject
    );
    if (!base?.length) return base;
    const { selectedRole, selectedFunctions, selectedGoals, selectedBrands } = userInfo;
    return base.map((pf: any) => ({
      ...pf,
      // selectedRole is cleared on workspace switch, so the saved portfolio role is
      // re-pointed at its role-master entry to keep the dropdown's selection valid
      profile: resolveSelectedRole(selectedRole ?? pf.profile, userPortfolioRoles),
      ...(selectedFunctions && { funcs: selectedFunctions }),
      ...(selectedGoals && { goals: selectedGoals }),
      ...(selectedBrands && { brands: selectedBrands }),
    }));
  }, [
    userInfo?.userPortfolios,
    userInfo?.preferredProject,
    userInfo?.selectedRole,
    userInfo?.selectedFunctions,
    userInfo?.selectedGoals,
    userInfo?.selectedBrands,
    userPortfolioRoles,
  ]);

  const userProfileDetails = useMemo(() => buildUserProfileDetails(userInfo), [
    userInfo?.userPortfolios,
    userInfo?.preferredProject,
    userInfo?.selectedRole,
    userInfo?.selectedBrands,
    userInfo?.selectedFunctions,
    userInfo?.selectedGoals,
  ]);

  const getTeamName = () => {
    try {
      if (!userInfo?.company_data || !userInfo?.preferredCompany || !userInfo?.preferredProject) {
        return TTYD_3;
      }
      const company = userInfo.company_data.find((c: any) => c.companyId === userInfo.preferredCompany);
      if (!company) return TTYD_3;
      const projectIdentifier = Array.isArray(userInfo.preferredProject)
        ? userInfo.preferredProject[0]?.project_name || userInfo.preferredProject[0]
        : userInfo.preferredProject;
      const project = company.projectList?.find((p: any) =>
        p.project_id === projectIdentifier || p.project_name === projectIdentifier
      );
      if (!project) return TTYD_3;
      return project.team_name || project.project_name || project.project_id;
    } catch {
      return TTYD_3;
    }
  };

  // Capture edit-mode at mount so key and portfolio override are stable for this lifecycle
  const [isEditMode] = useState<boolean>(() => editProfileTriggered);
  const [editModeStamp] = useState<number>(() => Date.now());
  // Scope the key to the workspace: the package seeds its selected role once
  // (prev ?? defaultRole) and never re-syncs, so a switch needs a remount for the
  // dropdown to pick up the new workspace's saved role.
  const homeKey = useMemo(() =>
    isEditMode
      ? `home-edit-${userInfo?.preferredProject}-${editModeStamp}`
      : `home-${userInfo?.preferredProject}`,
    [isEditMode, editModeStamp, userInfo?.preferredProject]
  );

  // Roles are gated alongside funcs/goals: the package seeds its selected role on
  // first render and never re-syncs, so the list has to be in before Home mounts.
  useEffect(() => {
    setIsFuncsLoading(true);
    Promise.all([
      dispatch(fetchFunctionsAndGoals() as any) as Promise<any>,
      dispatch(fetchRoles() as any) as Promise<any>,
    ]).finally(() => {
      setIsFuncsLoading(false);
    });
  }, [dispatch]);

  // Reset the flag once Home has mounted in edit mode so future mounts behave normally
  useEffect(() => {
    if (isEditMode) {
      dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'editProfileTriggered', value: false } });
    }
  }, []);

  // Watch for DOM transitions inside the MFE home wrapper:
  // - .chat-container appearing means the chat view is active
  // - .homepage-dashboard appearing/disappearing tracks whether HomepageDashboard is rendered
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let chatActive = false;

    const observer = new MutationObserver(() => {
      const hasChat = !!container.querySelector('.chat-container');
      if (hasChat && !chatActive) {
        chatActive = true;
        dispatch({ type: SET_ACTIVE_MENU, payload: 'newChat' });
      }
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [dispatch]);

  const apps = useMemo(() => activeApp ? [activeApp] : [], [activeApp]);

  if (!Home) return null;

  if (isFuncsLoading || isUserGroupDataPending) return <div className="home-container" ref={containerRef}><Loader /></div>;

  return (
    <div className="home-container" ref={containerRef}>
    <Home
      key={homeKey}
      DOMAIN={baseURL}
      DOMAIN_MIDDLEWARE={middlewareURL}
      DOMAIN_UM={UMUrl}
      PROJECT_ID={userInfo?.preferredProject}
      USERNAME={`${userInfo?.first_name || ''}`}
      COMPANY_ID={userInfo?.preferredCompany}
      TEAM={getTeamName()}
      KNOWLEDGE_ID={activeKnowledgeId}
      APPS={apps}
      OPEN_PROFILE_EDIT={isEditMode}
      USER_PORTFOLIO={userPortfolio}
      PROJECT_BRAND_LIST={projectBrandList}
      IS_WORKSPACE_OWNER={isWorkspaceOwner}
      USER_ID={userInfo?.id}
      USER_PORTFOLIO_ROLES={userPortfolioRoles}
      DROPDOWN_DATA_FUNCS_GOALS={functionsAndGoals?.data_value}
      USER_GROUPS={userGroupNames}
      ONBOARDING_ENABLED={isOnboardingEnabled}
      USER_PROFILE_DETAILS={userProfileDetails}
      showOnBoarding={pathname === '/'}
      onProfileSave={(data:any) => {
        dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'selectedRole', value: data.profile } });
        dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'selectedFunctions', value: data.funcs } });
        dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'selectedGoals', value: data.goals } });
        dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'selectedBrands', value: data.brands } });
        if (data.portfolio_id) {
          const existingPortfolios: any[] = userInfo?.userPortfolios || [];
          const projectId = userInfo?.preferredProject;
          const portfolioExists = existingPortfolios.some((pf: any) => pf.project_id == projectId);
          const updatedPortfolios = portfolioExists
            ? existingPortfolios.map((pf: any) =>
                pf.project_id == projectId ? { ...pf, portfolio_id: data.portfolio_id, is_complete: data.is_complete } : pf
              )
            : [...existingPortfolios, { project_id: projectId, portfolio_id: data.portfolio_id, is_complete: data.is_complete }];
          dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'userPortfolios', value: updatedPortfolios } });
        }
      }}
      onStepChange={(step:any) => {
        dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'isHomepageDashboard', value: step === 'homepage' } });
        if (step !== 'homepage') {
          dispatch({ type: SET_ACTIVE_MENU, payload: 'home' });
        }
        if (step === 'homepage') {
          const existingPortfolios: any[] = userInfo?.userPortfolios || [];
          const projectId = userInfo?.preferredProject;
          const portfolioExists = existingPortfolios.some((pf: any) => pf.project_id == projectId);
          const updatedPortfolios = portfolioExists
            ? existingPortfolios.map((pf: any) => pf.project_id == projectId ? { ...pf, is_complete: true } : pf)
            : [...existingPortfolios, { project_id: projectId, is_complete: true }];
          dispatch({ type: SET_USER_INFO_KEY_VALUE, payload: { key: 'userPortfolios', value: updatedPortfolios } });
        }
      }}
            />
    </div>
  );
};

export default HomeComponent;
